import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { format } from 'date-fns';
import { transactionService } from '../../services/transactionService';
import { balanceService } from '../../services/balanceService';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import SubNav from '../../components/SubNav/SubNav';
import './Wallet.css';
import '../../styles/Dashboard.css';

const TransactionType = {
  MONEY_ADDED: 'MONEY_ADDED',
  MONEY_USED: 'MONEY_USED',
  REFUND: 'REFUND',
  MINUTES_GRANTED: 'MINUTES_GRANTED',
  MINUTES_USED: 'MINUTES_USED'
};

const Wallet = () => {
  const [transactions, setTransactions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [balances, setBalances] = useState(null);
  const ITEMS_PER_PAGE = 10;
  const headerRef = useRef(null);

  const fetchBalances = async () => {
    try {
      const data = await balanceService.getBalances();
      setBalances(data);
    } catch (error) {
      console.error('Failed to fetch balances:', error);
    }
  };

  const fetchTransactions = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await transactionService.getTransactions(currentPage, ITEMS_PER_PAGE);
      setTransactions(prev => currentPage === 1 ? data : [...prev, ...data]);
      setHasMore(data.length === ITEMS_PER_PAGE);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    fetchBalances();
    fetchTransactions();
  }, [fetchTransactions]);

  const formatDate = (dateString) => {
    return format(new Date(dateString), 'MMM d, yyyy h:mm a');
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case TransactionType.MONEY_ADDED:
        return '💰';
      case TransactionType.MONEY_USED:
        return '📞';
      case TransactionType.REFUND:
        return '↩️';
      case TransactionType.MINUTES_GRANTED:
        return '🎁';
      case TransactionType.MINUTES_USED:
        return '⏱️';
      default:
        return '📝';
    }
  };

  const isCredit = (type) => {
    return [TransactionType.MONEY_ADDED, TransactionType.REFUND, TransactionType.MINUTES_GRANTED].includes(type);
  };

  return (
    <div className="dashboard-page">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Wallet | NowTarot AI</title>
      </Helmet>
      <Header ref={headerRef} />
      <SubNav />
      
      <div className="dashboard-container">
        {/* Balance & Payment Grid */}
        <div className="wallet-grid">
          {/* Balance Card */}
          <div className="dash-card">
            <div className="dash-card-header">
              <h3 className="dash-card-title">
                <span className="card-icon">💰</span>
                Balance
              </h3>
            </div>
            <div className="balance-display">
              <div className="balance-amount">
                ${Number(balances?.wallet_balance || 0).toFixed(2)}
              </div>
              <div className="balance-label">Available funds</div>
            </div>
          </div>

          {/* Payments Disabled Card */}
          <div className="dash-card">
            <div className="dash-card-header">
              <h3 className="dash-card-title">
                <span className="card-icon">💳</span>
                Payments
              </h3>
            </div>
            <div className="empty-state" style={{ padding: '1.5rem 0' }}>
              <div className="empty-state-icon">💳</div>
              <div className="empty-state-text">Payments are currently unavailable.</div>
              <p className="wallet-hint" style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: '#6B7280', textAlign: 'center' }}>
                Calling is disabled, so wallet top-ups and refunds are disabled too.
              </p>
            </div>
          </div>
        </div>

        {/* Transactions */}
        <div className="dash-card">
          <div className="dash-card-header">
            <h3 className="dash-card-title">
              <span className="card-icon">📋</span>
              Transaction History
            </h3>
          </div>
          
          {transactions.length === 0 && !isLoading ? (
            <div className="empty-state">
              <div className="empty-state-icon">📝</div>
              <div className="empty-state-text">No transactions yet</div>
            </div>
          ) : (
            <div className="transaction-list">
              {transactions.map((transaction) => (
                <div className="transaction-item" key={transaction.id}>
                  <div className={`transaction-icon ${isCredit(transaction.type) ? 'credit' : 'debit'}`}>
                    {getTransactionIcon(transaction.type)}
                  </div>
                  <div className="transaction-details">
                    <div className="transaction-title">
                      {transaction.type.split('_').map(word => 
                        word.charAt(0) + word.slice(1).toLowerCase()
                      ).join(' ')}
                    </div>
                    <div className="transaction-date">
                      {formatDate(transaction.created_at)}
                      {transaction.description && ` • ${transaction.description}`}
                    </div>
                  </div>
                  <div className="transaction-right">
                    {[TransactionType.MINUTES_GRANTED, TransactionType.MINUTES_USED].includes(transaction.type) ? (
                      <div className="transaction-amount credit">
                        {transaction.minutes_amount} min
                      </div>
                    ) : (
                      <div className={`transaction-amount ${isCredit(transaction.type) ? 'credit' : 'debit'}`}>
                        {isCredit(transaction.type) ? '+' : '-'}${Math.abs(transaction.amount).toFixed(2)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {isLoading && <div className="loading-spinner">Loading...</div>}
          
          {!isLoading && hasMore && transactions.length > 0 && (
            <button 
              className="btn btn-secondary btn-full"
              onClick={() => setCurrentPage(prev => prev + 1)}
              style={{ marginTop: '1rem' }}
            >
              Load More
            </button>
          )}
        </div>

        <div className="dash-footer">
          Payments are disabled while calling is unavailable.
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Wallet;
